import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { BasisTheoryService } from '../../../services/basistheory';
import { PaymentProcessService } from '../../../services/payment-process.service';
import { AuthService } from '../../../../auth';

@Component({
  selector: 'kt-bridgepay-credit-card-form',
  templateUrl: './credit-card-form.component.html',
  styleUrls: ['./credit-card-form.component.scss'],
})
export class CreditCardFormComponent implements OnInit, OnDestroy {
  @Output() formSubmit = new EventEmitter<{
    token: any;
    type: string;
    brand: string;
    savePaymentMethod: boolean;
    zip: string;
    cardLast4: string;
    alias: string;
  }>();

  @Input() tenantId: string;
  @Input() showSubmitButton = true;
  @Input() showSavePaymentMethodOption = false;

  cardNumberEmpty = false;
  cardNameEmpty = false;
  expirationDateEmpty = false;
  cvcEmpty = false;
  aliasEmpty = false;
  aliasValue = ''; // Add a property to bind to the input

  private btInstance?: any;
  private cardNameElement?: any;
  private zipCodeElement?: any;
  private cardNumberElement?: any;
  private cardExpirationDateElement?: any;
  private cardVerificationCodeElement?: any;

  brand = '';

  isSubmitting = false;
  validationMessage: string | null = null;
  savePaymentMethod = false;

  private readyStates: { [key: string]: boolean } = {};

  constructor(
    private basisTheoryService: BasisTheoryService,
    private processPaymentService: PaymentProcessService,
    private authService: AuthService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.processPaymentService.updateField('payment_method', null);

    this.processPaymentService.triggerGenerateToken$.subscribe(() => {
      this.onSubmit();
    });

    try {
      this.btInstance = await this.basisTheoryService.initialize(
        this.authService.bridgepayApiPciApiKey,
      );

      // Create elements
      this.cardNameElement = this.createElement('text', 'cardNameElement', {
        autoComplete: 'cc-name',
      });

      this.zipCodeElement = this.createElement('text', 'zipCodeElement', {
        autoComplete: 'postal-code',
        inputMode: 'numeric',
      });

      this.cardNumberElement = this.createElement('cardNumber', 'cardNumberElement', {
        placeholder: '',
        autoComplete: 'cc-number',
        mask: [
          /\d/,
          /\d/,
          /\d/,
          /\d/,
          ' ',
          /\d/,
          /\d/,
          /\d/,
          /\d/,
          ' ',
          /\d/,
          /\d/,
          /\d/,
          /\d/,
          ' ',
          /\d/,
          /\d/,
          /\d/,
          /\d/,
        ],
        transform: /[\s-]/g,
      });

      this.cardExpirationDateElement = this.createElement(
        'cardExpirationDate',
        'cardExpirationDateElement',
        {
          placeholder: '',
          autoComplete: 'cc-exp',
        },
      );

      this.cardVerificationCodeElement = this.createElement(
        'cardVerificationCode',
        'cardVerificationCodeElement',
        {
          placeholder: '',
          autoComplete: 'cc-csc',
        },
      );

      // Mount elements
      await Promise.all([
        this.cardNameElement.mount('#cardNameElement'),
        this.zipCodeElement.mount('#zipCodeElement'),
        this.cardNumberElement.mount('#cardNumberElement'),
        this.cardExpirationDateElement.mount('#cardExpirationDateElement'),
        this.cardVerificationCodeElement.mount('#cardVerificationCodeElement'),
      ]);

      // For card name and zip code (no custom callback needed)
      this.setupAutoSubmitListener(this.cardNameElement);
      this.setupAutoSubmitListener(this.zipCodeElement);

      // For card number: update the brand and propagate it to the CVC element
      this.setupAutoSubmitListener(this.cardNumberElement, (event: any) => {
        this.brand = event.cardBrand;
        this.cardVerificationCodeElement.update({ cardBrand: event.cardBrand });
      });

      // For expiration date and CVC
      this.setupAutoSubmitListener(this.cardExpirationDateElement);
      this.setupAutoSubmitListener(this.cardVerificationCodeElement);

      // For card name and zip code (no custom callback needed)
      this.setupReadyListener(this.cardNameElement, 'cardName');
      this.setupReadyListener(this.zipCodeElement, 'zipCode');

      // For card number: update the brand then mark as ready
      this.setupReadyListener(this.cardNumberElement, 'cardNumber');

      // For expiration date and CVC
      this.setupReadyListener(this.cardExpirationDateElement, 'cardExpirationDate');
      this.setupReadyListener(this.cardVerificationCodeElement, 'cardVerificationCode');
    } catch (error) {
      this.handleError(error);
    }
  }

  private createElement(type: string, targetId: string, config: any): any {
    return this.btInstance?.createElement(type, {
      ...config,
      targetId,
      style: this.basisTheoryService.COMMON_STYLE,
    });
  }

  async onSubmit(): Promise<void> {
    this.aliasEmpty = !this.aliasValue;
    if (this.checkFormEmpty() || this.aliasEmpty) return;

    this.isSubmitting = true;
    this.validationMessage = null;

    await this.generateToken();
  }

  async generateToken(): Promise<void> {
    try {
      const tenantContainerPath = `/tenant-${this.tenantId.toLowerCase()}/pci/`; // Normalize to lowercase as per Basis Theory docs

      const token = await this.btInstance?.tokens.create({
        type: 'card',
        data: {
          name: this.cardNameElement,
          number: this.cardNumberElement,
          expiration_month: this.cardExpirationDateElement.month(),
          expiration_year: this.cardExpirationDateElement.year(),
          cvc: this.cardVerificationCodeElement,
          zip: this.zipCodeElement,
        },
        fingerprintExpression: '{{ data.number }}',
        deduplicateToken: true,
        mask: {
          number: '{{ data.number | card_mask: "true", "true" }}',
        },
        // *** THIS IS THE KEY CHANGE ***
        containers: [tenantContainerPath],
      });

      this.formSubmit.emit({
        token,
        type: 'card',
        brand: this.cardNumberElement?._cardMetadata?.cardBrand,
        savePaymentMethod: this.savePaymentMethod,
        zip: this.zipCodeElement.value,
        cardLast4: this.cardNumberElement?._cardMetadata?.cardLast4,
        alias: this.aliasValue,
      });
    } catch (error) {
      this.handleError(error);
    } finally {
      this.isSubmitting = false;
    }
  }

  private setupAutoSubmitListener(element: any, customCallback?: (event: any) => void): void {
    element.on('change', (event: any) => {
      if (customCallback) customCallback(event);
      this.resetForm();
    });
  }

  private handleError(error: any): void {
    this.validationMessage = error.data?.detail || 'Payment processing failed. Please try again.';
  }

  private setupReadyListener(
    element: any,
    key: string,
    customCallback?: (event: any) => void,
  ): void {
    element.on('ready', (event: any) => {
      // Mark the element as ready
      this.readyStates[key] = true;
      if (customCallback) {
        customCallback(event);
      }
    });
  }

  resetForm() {
    this.cardNameEmpty = false;
    this.cardNumberEmpty = false;
    this.expirationDateEmpty = false;
    this.cvcEmpty = false;
  }

  ngOnDestroy(): void {
    [
      this.cardNameElement,
      this.zipCodeElement,
      this.cardNumberElement,
      this.cardExpirationDateElement,
      this.cardVerificationCodeElement,
    ].forEach((element) => element?.unmount());
  }

  checkFormEmpty(): boolean {
    this.cardNameEmpty = this.cardNameElement?.metadata.empty;
    this.cardNumberEmpty = this.cardNumberElement?.metadata.empty;
    this.expirationDateEmpty = this.cardExpirationDateElement?.metadata.empty;
    this.cvcEmpty = this.cardVerificationCodeElement?.metadata.empty;
    return this.cardNameEmpty && this.cardNumberEmpty && this.expirationDateEmpty && this.cvcEmpty;
  }

  protected readonly close = close;
}
